import { z } from 'zod';
import { BODY_FORMATS } from '../lib/body';
import { INQUIRY_STATUSES } from '../lib/constants';

/** One message in a batch. The planner has seen and possibly edited each of
 *  these individually, so subject and body arrive fully rendered — the server
 *  re-validates for unresolved merge variables but does not re-merge. */
/**
 * How long a body may be, by format.
 *
 * The plain-text ceilings are the ones that were here before rich bodies
 * existed and are deliberately unchanged: a 25k plain-text reply was too long
 * to be a reply then and still is. An html body of the same prose is several
 * times the bytes — every paragraph carries its own tags — so it gets its own
 * ceiling rather than forcing the text one up to meet it. Images cost nothing
 * against either: they are stored bytes referenced by a short url.
 */
export const BODY_MAX_CHARS = {
  composed: { text: 50_000, html: 200_000 },
  reply: { text: 25_000, html: 120_000 },
} as const;

export const composedMessageSchema = z
  .object({
    vendorId: z.string().uuid(),
    subject: z.string().min(1).max(255),
    body: z.string().min(1).max(BODY_MAX_CHARS.composed.html),
    /** How to read `body`. An HTML body is re-sanitized server-side before it
     *  is queued — the editor's schema constrains what a planner can build,
     *  but this endpoint accepts whatever a client sends. */
    bodyFormat: z.enum(BODY_FORMATS).default('text'),
  })
  .superRefine((value, ctx) => {
    const limit = BODY_MAX_CHARS.composed[value.bodyFormat];
    if (value.body.length > limit) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: limit,
        type: 'string',
        inclusive: true,
        path: ['body'],
        message: 'That message is too long to send.',
      });
    }
  });

export const createBatchSchema = z.object({
  weddingId: z.string().uuid(),
  category: z.string().min(1).max(100),
  messages: z.array(composedMessageSchema).min(1).max(25),
});

export const updateInquiryStatusSchema = z.object({
  status: z.enum(INQUIRY_STATUSES),
});

export const updateInquirySchema = z.object({
  status: z.enum(INQUIRY_STATUSES).optional(),
  /**
   * A number is the planner's own figure and outranks every extraction from
   * then on. `null` is not "store nothing" — it is a *rejection*: the server
   * stamps `rejectedAt` on whichever row produced the figure that was on
   * screen, so the next resolve cannot hand the same number straight back.
   * See `rejectResolved` in `backend/src/lib/quote-headline.ts`.
   */
  quoteAmount: z.number().min(0).nullable().optional(),
  quoteCurrency: z.string().length(3).optional(),
  /**
   * Which package of a multi-tier quote the planner is actually considering —
   * and the headline does not follow it. Resolution reads `isSelected`
   * nowhere: a multi-tier quote resolves to the span of its priced packages,
   * precisely because picking one of them was a choice nobody had made. What
   * this records is the choice when the planner does make it, by moving the
   * mark within that quote — one package carries it at a time — and nothing
   * more.
   *
   * No route reads the field, so nothing checks that the id belongs to this
   * inquiry. That check lives in `selectPackage`
   * (`backend/src/lib/quote-headline.ts`), written and reachable from nothing:
   * it scopes the move to the id's own quote and resolves the id against the
   * evidence already loaded for this inquiry, so a package on another
   * planner's inquiry is a false return rather than a write. Phase 2's review
   * editor is its intended caller, and this field is the shape that caller
   * will send.
   */
  selectedPackageId: z.string().uuid().optional(),
});

/** A quick reply typed in the app. See `BODY_MAX_CHARS` for why the ceiling
 *  depends on the format rather than being one number for both. */
export const replyToInquirySchema = z
  .object({
    text: z.string().trim().min(1).max(BODY_MAX_CHARS.reply.html),
    /** How to read `text`. Older clients omit it and keep sending plain text. */
    bodyFormat: z.enum(BODY_FORMATS).default('text'),
    /** Which of the inquiry's Gmail threads to answer. Omitted by older
     *  clients, which get the inquiry's original thread. */
    gmailThreadId: z.string().min(1).max(64).optional(),
  })
  .superRefine((value, ctx) => {
    const limit = BODY_MAX_CHARS.reply[value.bodyFormat];
    if (value.text.length > limit) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: limit,
        type: 'string',
        inclusive: true,
        path: ['text'],
        message: 'That reply is too long to send.',
      });
    }
  });

export const bulkUpdateStatusSchema = z.object({
  inquiryIds: z.array(z.string().uuid()).min(1).max(200),
  status: z.enum(INQUIRY_STATUSES),
});

export type ComposedMessage = z.infer<typeof composedMessageSchema>;
export type CreateBatchInput = z.infer<typeof createBatchSchema>;
export type UpdateInquiryInput = z.infer<typeof updateInquirySchema>;
export type BulkUpdateStatusInput = z.infer<typeof bulkUpdateStatusSchema>;
export type ReplyToInquiryInput = z.infer<typeof replyToInquirySchema>;
